import { useState, useEffect } from "react";
import { ArrowLeft, Scale, Trash2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where, deleteDoc, writeBatch } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { filterCases, describeCard } from "../utils/metricEngine";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";
import CaseSearchFilter from "../components/CaseSearchFilter";
import PageHeader from "../components/PageHeader";

const MetricCases = () => {
    const { globalUser } = useAuth();
    const { id } = useParams();
    const [cardDetails, setCardDetails] = useState({});
    const [pendingCases, setPendingCases] = useState([]);
    const [disposedCases, setDisposedCases] = useState([]);
    const [selectedType, setSelectedType] = useState(true);
    const [filterPendingCases, setFilterPendingCases] = useState([]);
    const [filterDisposedCases, setFilterDisposedCases] = useState([]);
    const [showDisposedCases, setShowDisposedCases] = useState(false);
    const [loading, setLoading] = useState(true);
    const [switchingView, setSwitchingView] = useState(false);
    const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchFiltered, setSearchFiltered] = useState([]);

    // The list currently shown (pending or disposed), fed into the search/filter bar.
    const activeCases = selectedType ? filterPendingCases : filterDisposedCases;

    const cleanupEventsAndReminders = async (caseNumber, userId) => {
        if (!caseNumber || !userId) return;
        try {
            const batch = writeBatch(db);
            const eventsQuery = query(
                collection(db, "lawyers", userId, "events"),
                where("Case No", "==", caseNumber)
            );
            const eventsSnapshot = await getDocs(eventsQuery);
            eventsSnapshot.docs.forEach((eventDoc) => batch.delete(eventDoc.ref));

            const remindersQuery = query(
                collection(db, "eventReminders"),
                where("caseNo", "==", caseNumber),
                where("recipientId", "==", userId)
            );
            const remindersSnapshot = await getDocs(remindersQuery);
            remindersSnapshot.docs.forEach((reminderDoc) => batch.delete(reminderDoc.ref));

            if (eventsSnapshot.size > 0 || remindersSnapshot.size > 0) {
                await batch.commit();
            }
        } catch (error) {
            console.error("Error cleaning up events and reminders:", error);
        }
    };

    const handleDeleteAll = async () => {
        setShowDeleteAllConfirmation(false);
        setIsDeleting(true);

        const currentCases = selectedType ? filterPendingCases : filterDisposedCases;
        const collectionName = selectedType ? "pending" : "disposed";

        try {
            if (globalUser?.uid && currentCases.length > 0) {
                await Promise.all(
                    currentCases
                        .map((c) => c.caseNumber || c.caseNo)
                        .filter(Boolean)
                        .map((caseNumber) => cleanupEventsAndReminders(caseNumber, globalUser.uid))
                );
            }

            await Promise.all(
                currentCases.map((c) => deleteDoc(doc(db, collectionName, c.id)))
            );

            if (selectedType) {
                setFilterPendingCases([]);
                setPendingCases(prev => prev.filter(c => !currentCases.some(fc => fc.id === c.id)));
            } else {
                setFilterDisposedCases([]);
                setDisposedCases(prev => prev.filter(c => !currentCases.some(fc => fc.id === c.id)));
            }
        } catch (error) {
            console.error("Error deleting all cases:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        const fetchMetricData = async () => {
            if (!globalUser || !id) return;

            try {
                setLoading(true);

                // First, get the metric card details
                const docRef = doc(db, "metricCard", id);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    console.log("Metric card not found");
                    setLoading(false);
                    return;
                }

                const cardData = docSnap.data();
                setCardDetails(cardData);

                // Fetch all pending cases
                const pendingCollection = collection(db, "pending");
                const pendingQuery = query(pendingCollection, where("owner", "==", globalUser.uid));
                const pendingSnapshot = await getDocs(pendingQuery);

                const pendingList = pendingSnapshot.docs.map(doc => ({
                    id: doc.id,
                    caseNumber: doc.data().caseNo,
                    type: doc.data().caseType,
                    petitionerName: doc.data().petitionerName,
                    court: doc.data().courtName,
                    status: doc.data().status || "Status not available",
                    nextHearingDate: doc.data().nextHearingDate || null,
                    caseFiledDate: doc.data().caseFiledDate || null,
                    labels: doc.data().labels || [],
                    labelsByUser: doc.data().labelsByUser || {},
                    ...doc.data()
                }));
                setPendingCases(pendingList);

                // Fetch all disposed cases
                const disposedCollection = collection(db, "disposed");
                const disposedQuery = query(disposedCollection, where("owner", "==", globalUser.uid));
                const disposedSnapshot = await getDocs(disposedQuery);

                const disposedList = disposedSnapshot.docs.map(doc => ({
                    id: doc.id,
                    caseNumber: doc.data().caseNo,
                    type: doc.data().caseType,
                    petitionerName: doc.data().petitionerName,
                    court: doc.data().courtName,
                    status: doc.data().status || "Status not available",
                    nextHearingDate: doc.data().nextHearingDate || null,
                    caseFiledDate: doc.data().caseFiledDate || null,
                    labels: doc.data().labels || [],
                    labelsByUser: doc.data().labelsByUser || {},
                    ...doc.data()
                }));
                setDisposedCases(disposedList);

                // Filter cases based on metric type
                filterCasesByMetric(cardData, pendingList, disposedList);

            } catch (error) {
                console.error("Error fetching metric data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMetricData();
    }, [globalUser, id]);

    // Filter both lists through the shared metric engine so the cases shown
    // here always match the count rendered on the dashboard card.
    const filterCasesByMetric = (cardData, pendingList, disposedList) => {
        const ctx = { uid: globalUser.uid };
        setFilterPendingCases(filterCases(pendingList, cardData, ctx));
        setFilterDisposedCases(filterCases(disposedList, cardData, ctx));
        setShowDisposedCases(true);
    };

    // Reset to page 1 whenever the filtered result set changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchFiltered]);

    const handleViewSwitch = async (isPending) => {
        if (selectedType === isPending) return; // Don't switch if already selected

        setSwitchingView(true);
        // Add a small delay to show the loading skeleton
        await new Promise(resolve => setTimeout(resolve, 300));
        setSelectedType(isPending);
        setSwitchingView(false);
    };

    const getMetricTitle = () => cardDetails.title || describeCard(cardDetails) || "Metric Cases";

    const getCaseLink = (caseItem) => {
        // Determine the correct collection based on case status or type
        if (pendingCases.some(pending => pending.id === caseItem.id)) {
            return `/portal/case/${caseItem.id}`;
        } else if (disposedCases.some(disposed => disposed.id === caseItem.id)) {
            return `/portal/case/${caseItem.id}`;
        }
        return `/portal/case/${caseItem.id}`;
    };

    const CaseCardSkeleton = () => (
        <div className={`${isLightMode ? "bg-white border border-gray-200" : "bg-[#1e1b4b]"} rounded-lg p-4`}>
            <div className="flex">
                <div className="mr-4 flex items-center">
                    <div className="w-16 h-16 flex items-center justify-center">
                        <Skeleton circle height={48} width={48} baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} />
                    </div>
                </div>
                <div className="flex-1">
                    <Skeleton height={16} width="60%" baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} className="mb-2" />
                    <Skeleton height={20} width="80%" baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} className="mb-2" />
                    <Skeleton height={16} width="70%" baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} className="mb-1" />
                    <Skeleton height={16} width="65%" baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} className="mb-1" />
                    <Skeleton height={16} width="55%" baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"} />
                </div>
            </div>
        </div>
    );

    const MetricCardSkeleton = () => (
        <div className="rounded-lg p-4 text-center flex-1">
            <Skeleton height={20} width="70%" baseColor="#4a4a4a" highlightColor="#6a6a6a" className="mb-1" />
            <Skeleton height={28} width="40%" baseColor="#4a4a4a" highlightColor="#6a6a6a" />
        </div>
    );

    const isLightMode = document.body.classList.contains("light-mode");

    if (loading) {
        return (
            <SkeletonTheme
    baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
    highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"}
            >
                <div className="min-h-screen bg-[#0c0a29] text-white relative">
                    <Navbar />
                    <div className="background-circle circle-3 z-0"></div>
                    <div className="background-circle circle-4 z-0"></div>

                    <main className="px-4 md:px-16 py-12">
                        {/* Header Skeleton */}
                        <div className="flex items-center mb-6">
                            <div className="flex items-center md:ml-12">
                                <ArrowLeft style={{ color: "white" }} />
                                <div className="ml-4">
                                    <Skeleton height={32} width={200} baseColor="#4a4a4a" highlightColor="#6a6a6a" />
                                </div>
                            </div>
                        </div>

                        {/* Metric Summary Skeleton */}
                        <div className="flex flex-col items-center">

                            <div className="flex items-stretch justify-center gap-4 mb-10 mt-1 w-full max-w-md">
                                <MetricCardSkeleton />
                                <MetricCardSkeleton />
                            </div>
                        </div>

                        {/* Case Cards Skeleton */}
                        <div className="md:px-12">
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <CaseCardSkeleton key={index} />
                                ))}
                            </div>
                        </div>
                    </main>
                </div>
            </SkeletonTheme>
        );
    }

    return (
        <div className="min-h-screen bg-[#0c0a29] text-white relative">
            <Navbar />
            <div className="background-circle circle-3 z-0"></div>
            <div className="background-circle circle-4 z-0"></div>

            <main className="px-4 md:px-16 py-12">
                <PageHeader
                    title={getMetricTitle()}
                    subtitle="Filtered cases"
                    actions={
                        activeCases.length > 0 && (
                            <>
                                <button
                                    onClick={() => setShowDeleteAllConfirmation(true)}
                                    disabled={isDeleting}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors duration-150 disabled:opacity-50"
                                    title="Delete All Cases"
                                >
                                    <Trash2 size={14} />
                                    Delete All
                                </button>
                                <CaseSearchFilter
                                    cases={activeCases}
                                    onFilteredChange={setSearchFiltered}
                                    className="w-40 sm:w-56 md:w-72"
                                />
                            </>
                        )
                    }
                />

                {/* Metric Summary Cards */}
                <div className="flex flex-col items-center">

                    <div className="flex items-stretch justify-center gap-4 mb-10 mt-1 w-full max-w-md">
                        <div
                            className={`${selectedType ? "bg-yellow-300 bg-opacity-60" : "bg-white bg-opacity-80"} rounded-lg p-4 hover:bg-opacity-80 transition cursor-pointer text-center flex-1`}
                            onClick={() => handleViewSwitch(true)}
                        >
                            <h3 className="text-black text-base md:text-lg mb-1">Pending Cases</h3>
                            <p className="text-black text-xl md:text-2xl font-bold">
                                {filterPendingCases.length}
                            </p>
                        </div>

                        {showDisposedCases && (
                            <div
                                className={`${!selectedType ? "bg-yellow-300 bg-opacity-60" : "bg-white bg-opacity-60"} rounded-lg p-4 hover:bg-opacity-80 transition cursor-pointer text-center flex-1`}
                                onClick={() => handleViewSwitch(false)}
                            >
                                <h3 className="text-base md:text-lg mb-1 text-black">Disposed Cases</h3>
                                <p className="text-xl md:text-2xl font-bold text-black">
                                    {filterDisposedCases.length}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Case Cards */}
                <div className="md:px-12">
                    {switchingView ? (
                        <SkeletonTheme
    baseColor={isLightMode ? "#d1d5db" : "#2d2a5a"}
    highlightColor={isLightMode ? "#e5e7eb" : "#3a3666"}
                        >
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.from({ length: 3 }).map((_, index) => (
                                    <CaseCardSkeleton key={index} />
                                ))}
                            </div>
                        </SkeletonTheme>
                    ) : (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {searchFiltered.length > 0 ? searchFiltered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((caseItem) => (
                                <Link
                                    key={caseItem.id}
                                    to={getCaseLink(caseItem)}
                                    className="bg-[#1e1b4b] rounded-lg p-4 hover:bg-[#2d2a5a] transition-colors duration-200"
                                >
                                    <div className="flex">
                                        <div className="mr-4 flex items-center">
                                            <div className="w-16 h-16 flex items-center justify-center">
                                                <Scale size={48} color="#FFD700" strokeWidth={1.5} />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-400">
                                                Case Number: {caseItem.caseNumber || 'N/A'}
                                            </p>
                                            <h3 className="text-xl font-bold">{caseItem.type || 'N/A'}</h3>
                                            <p>Party Name: {caseItem.petitionerName || 'N/A'}</p>
                                            <p>Court: {caseItem.court || 'N/A'}</p>
                                            <p>Status: {caseItem.status}</p>
                                            {caseItem.nextHearingDate && (
                                                <p className="text-yellow-400">Next Hearing: {caseItem.nextHearingDate}</p>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            )) : (
                                <div className="col-span-full text-center text-gray-400 py-8">
                                    {activeCases.length > 0
                                        ? "No cases match your current filters."
                                        : `No ${selectedType ? "pending" : "disposed"} cases found for this metric`}
                                </div>
                            )}
                        </div>
                    )}
                    <Pagination
                        currentPage={currentPage}
                        totalItems={searchFiltered.length}
                        onPageChange={(page) => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                    />
                </div>
            </main>

            {/* Delete All Confirmation Modal */}
            {showDeleteAllConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
                    <div className="bg-white text-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h3 className="text-lg font-bold mb-3">Delete All {selectedType ? "Pending" : "Disposed"} Cases</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete all{" "}
                            <span className="font-semibold">
                                {selectedType ? filterPendingCases.length : filterDisposedCases.length}
                            </span>{" "}
                            {selectedType ? "pending" : "disposed"} case(s) from this metric? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteAllConfirmation(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAll}
                                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                            >
                                Delete All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetricCases;